import { FormProvider, useController } from 'react-hook-form';
import { deliveryRatioColor, } from 'root/src/shared/constants/project';
import { colorsNew } from 'root/src/client/web/commonStyles';
import { useMemo, memo } from 'react';
import Icon from 'root/src/client/web/base/Icon';
import { platformIconMap } from 'root/src/shared/constants/dropKeys';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import DareDropIcon from 'root/src/client/public/icons/DareDropIcon';
import { ButtonNew, ButtonNewVariant } from 'root/src/client/v2/common/components/form/ButtonNew';
import { DEFAULT_MODAL_OPTIONS, ModalContextProvider, useModal } from 'root/src/client/v2/common/contexts/modal.context';
import { ModalLayout } from 'root/src/client/v2/common/layouts/ModalLayout';
import ProgressBar from 'root/src/client/web/base/ProgressBar';
import { useAccountData } from 'root/src/client/v2/userData/hooks/useAccountData';
import { useDeliveryRatio } from 'root/src/client/v2/userData/hooks/useDeliveryRatio';
import LoadingCircles from 'root/src/client/v2/common/components/LoadingCircles';
import * as Tile from 'root/src/client/v2/drop/features/KeySelectionModal/components/Tile';
import { KeySelectionModalContext, useDismissAlreadyOwnProductModal, useKeySelectionFormContext, useKeySelectionModal, useKeySelectionModalContext, } from 'root/src/client/v2/drop/features/KeySelectionModal/hooks/useKeySelectionModal';
import { isInitialBundle, isInitialCode, isInitialKey, } from 'root/src/shared/rewards/helpers/initialProduct.guards';
import { KeyProductMnemonicIdResolver, } from 'root/src/shared/rewards/services/productResolver.service';
import { TWITCH_MIN_STREAM_TIME_MINUTES } from 'root/src/shared/deliveryRatio/deliveryRatio.constants';
import { Platform } from 'root/src/shared/@types/platforms';
export const KeySelectionModal = memo((props) => {
    const { dropEvent } = props;
    const { form, onFormSubmit } = useKeySelectionModal(props);
    return (<ModalLayout className="sm:min-w-[600px]">
			{/* eslint-disable-next-line react/jsx-no-constructed-context-values */}
			<KeySelectionModalContext.Provider value={{ dropEvent }}>
				<FormProvider {...form}>
					<form onSubmit={onFormSubmit}>
						<div className="flex flex-col justify-center items-center gap-8">
							<KeySelectionModalHeading />
							<InitialProductSelectField />
							<DeliveryRateInfo />
							<ModalContextProvider>
								<UserHasGameSubmit onSubmit={onFormSubmit}/>
							</ModalContextProvider>
							<Submit />
						</div>
					</form>
				</FormProvider>
			</KeySelectionModalContext.Provider>
		</ModalLayout>);
});
export const KeySelectionModalHeading = memo(() => {
    return (<div className="flex flex-col justify-center items-center gap-1">
			<h1 className="font-bold text-2xl text-center">Select Your Platform</h1>
			<span className="text-center">This Drop is available on the platforms below. Click an icon to choose.</span>
		</div>);
});
export const PlatformField = memo(() => {
    const form = useKeySelectionFormContext();
    const { dropEvent } = useKeySelectionModalContext();
    const { initialProduct = [] } = dropEvent;
    const { field } = useController({
        name: 'productId',
        control: form.control,
    });
    const selectedPlatform = useMemo(() => {
        if (!field.value || !initialProduct.some(isInitialKey)) {
            return undefined;
        }
        return new KeyProductMnemonicIdResolver(field.value).platform;
    }, [field.value]);
    const platformOptions = useMemo(() => {
        return initialProduct
            .filter(isInitialKey)
            .reduce((acc, item) => {
            if (!isInitialKey(item)) {
                return acc;
            }
            const isDuplicate = acc.some((option) => {
                return new KeyProductMnemonicIdResolver(item.id).platform === new KeyProductMnemonicIdResolver(option.value).platform;
            });
            if (!isDuplicate) {
                acc.push({
                    label: new KeyProductMnemonicIdResolver(item.id).getReadableRegion(),
                    value: item.id,
                });
            }
            return acc;
        }, []);
    }, [initialProduct]);
    if (!platformOptions.length) {
        return null;
    }
    return (<ul className="flex flex-wrap justify-center gap-4">
			{platformOptions.map((option) => {
            const keyResolver = new KeyProductMnemonicIdResolver(option.value);
            const isSelected = selectedPlatform && selectedPlatform === keyResolver.platform;
            return (<li key={option.value}>
						<Tile.Root>
							<Tile.Button data-selected={isSelected} onClick={() => {
                    field.onChange(option.value);
                }}>
								<Icon color={colorsNew.dark3} fillColor={colorsNew.light3} textColor={colorsNew.dark3} size={54.5} icon={platformIconMap[keyResolver.platform]}/>
								<span className="text-dark-2 font-bold text-xs">{keyResolver.getReadablePlatform()}</span>
							</Tile.Button>
						</Tile.Root>
					</li>);
        })}
		</ul>);
});
const DeliveryRateInfo = memo(() => {
    const deliveryRatio = useDeliveryRatio();
    const accountData = useAccountData();
    const deliveryBarColor = getDeliveryRatioColor(deliveryRatio);
    return (<div className="flex flex-col justify-center items-center gap-2">
			<p className="font-bold text-lg uppercase text-center">Your delivery rate</p>
			<ProgressBar progress={deliveryRatio * 100} progressBarColor={deliveryBarColor} progressBackgroundColor={colorsNew.white} progressBackgroundStyle={{
            boxShadow: 'inset 0px 4px 4px rgba(0, 0, 0, 0.25)',
        }} progressBarStyle={{
            boxShadow: '2px 1px 4px rgba(0, 0, 0, 0.25)',
            minWidth: deliveryRatio > 0 ? 60 : 0,
        }} progressContentStyle={{
            boxShadow: 'inset 0px -1px 2px rgba(0, 0, 0, 0.25)',
        }}>
				<span className="text-sm text-black w-full text-right mx-2 tabular-nums">
					{`${(deliveryRatio * 100).toFixed(2)}%`}
				</span>
			</ProgressBar>
			<p className="text-center text-sm font-medium max-w-fit" style={{ color: colorsNew.green }}>
				Delivering without key increases delivery rate.
			</p>
			{((accountData === null || accountData === void 0 ? void 0 : accountData.preferredPlatform) === Platform.Twitch) && (<p className="text-center text-sm text-light-3 max-w-fit">
					To make a delivery you should stream at least {TWITCH_MIN_STREAM_TIME_MINUTES} minutes
				</p>)}
		</div>);
});
export const RegionField = memo(() => {
    const form = useKeySelectionFormContext();
    const { dropEvent } = useKeySelectionModalContext();
    const { initialProduct = [] } = dropEvent;
    const { field } = useController({
        name: 'productId',
        control: form.control,
    });
    const selectedPlatform = useMemo(() => {
        if (!field.value || !initialProduct.some(isInitialKey)) {
            return undefined;
        }
        return new KeyProductMnemonicIdResolver(field.value).platform;
    }, [field.value]);
    const regionOptions = useMemo(() => {
        return initialProduct.flatMap((item) => {
            if (isInitialKey(item)) {
                const keyResolver = new KeyProductMnemonicIdResolver(item.id);
                if (keyResolver.platform !== selectedPlatform) {
                    return [];
                }
                return {
                    label: keyResolver.region,
                    value: item.id,
                };
            }
            if (isInitialCode(item)) {
                return {
                    label: 'Worldwide',
                    value: item.id,
                };
            }
            return [];
        });
    }, [initialProduct, selectedPlatform]);
    if (!regionOptions.length) {
        return null;
    }
    return (<ul className="flex flex-wrap justify-center gap-4">
			{regionOptions.map((option) => {
            const isSelected = field.value === option.value;
            return (<li key={option.value}>
						<Tile.Root>
							<Tile.Button data-selected={isSelected} onClick={() => field.onChange(option.value)}>
								<span className="text-dark-2 uppercase font-bold text-xs">
									{option.label}
								</span>
							</Tile.Button>
						</Tile.Root>
					</li>);
        })}
		</ul>);
});
export const BundleField = memo(() => {
    const form = useKeySelectionFormContext();
    const { dropEvent } = useKeySelectionModalContext();
    const { initialProduct = [] } = dropEvent;
    const selectedProductId = form.watch('productId');
    const { field } = useController({
        name: 'productId',
        control: form.control,
    });
    const bundleOptions = useMemo(() => {
        return initialProduct.filter(isInitialBundle)
            .map(bundle => ({
            label: bundle.name,
            value: bundle.id,
        }));
    }, [initialProduct]);
    if (!bundleOptions.length) {
        return null;
    }
    return (<ul className="flex flex-wrap justify-center gap-4">
			{bundleOptions.map((option) => {
            const isSelected = selectedProductId === option.value;
            return (<li key={option.value}>
						<Tile.Root>
							<Tile.Button data-selected={isSelected} onClick={() => field.onChange(option.value)}>
								<span className="text-dark-2 uppercase font-bold text-xs">
									{option.label}
								</span>
							</Tile.Button>
						</Tile.Root>
					</li>);
        })}
		</ul>);
});
export const InitialProductSelectField = memo(() => {
    return (<div className="flex flex-col gap-4 justify-center items-center relative px-2 py-2">
			<BundleField />
			<PlatformField />
			<RegionField />
		</div>);
});
const UserHasGameDetailsModal = memo((props) => {
    const { onConfirm } = props;
    const [isAlreadyOwnProductModalDismissed, setIsAlreadyOwnProductModalDismissed] = useDismissAlreadyOwnProductModal();
    return (<ModalLayout className="z-[200]">
			<div className="flex flex-col gap-4 text-sm">
				<h1 className="font-bold text-xl">I already own product</h1>
				<p>
					Delivering without getting a product <strong>improves your delivery rate</strong>.
				</p>
				<p>
					You will still be able to deliver dares and earn rewards, but you will not receive a initial key for this drop.
				</p>
				<p>
					This option <strong>cannot be changed</strong> after dare creation.
				</p>
				<FormControlLabel control={(<Checkbox checked={isAlreadyOwnProductModalDismissed} onChange={(_, isChecked) => {
                setIsAlreadyOwnProductModalDismissed(isChecked);
            }}/>)} label={<span className="text-sm">Don't show this message again</span>}/>
				<ButtonNew className="font-bold uppercase" onClick={onConfirm}>I understand</ButtonNew>
			</div>
		</ModalLayout>);
});
const UserHasGameSubmit = memo(({ onSubmit }) => {
    const [dismissAlreadyOwnProductModal] = useDismissAlreadyOwnProductModal();
    const form = useKeySelectionFormContext();
    const modal = useModal();
    const isLoading = form.formState.isSubmitting;
    const userHasGame = form.watch('userHasGame');
    return (<ButtonNew variant={ButtonNewVariant.Secondary} disabled={isLoading} type="button" className="flex justify-center items-center gap-1" onClick={() => {
            form.setValue('userHasGame', true);
            if (dismissAlreadyOwnProductModal) {
                onSubmit();
                return;
            }
            modal.show(<UserHasGameDetailsModal onConfirm={() => {
                    onSubmit();
                    modal.hide();
                }}/>, { isCloseable: false, zIndex: DEFAULT_MODAL_OPTIONS.zIndex + 1 });
        }}>
			{(isLoading && userHasGame) && <LoadingCircles width={33} height={33}/>}
			I already own this Game
		</ButtonNew>);
});
const Submit = memo(() => {
    const form = useKeySelectionFormContext();
    const { dropEvent } = useKeySelectionModalContext();
    const { initialProduct = [] } = dropEvent;
    const isKey = initialProduct.some(isInitialKey);
    const isCode = initialProduct.some(isInitialCode);
    const isBundle = initialProduct.some(isInitialBundle);
    const userHasGame = form.watch('userHasGame');
    const isLoading = form.formState.isSubmitting;
    const isDisabled = isLoading || !form.formState.isValid;
    const label = useMemo(() => {
        if (isKey) {
            return 'Create dare and get key!';
        }
        if (isCode) {
            return 'Create dare and get code!';
        }
        if (isBundle) {
            return 'Create dare and get bundle!';
        }
        return 'Create dare and get product!';
    }, [isKey, isCode, isBundle]);
    if (userHasGame) {
        return null;
    }
    return (<ButtonNew disabled={isDisabled} className="flex justify-center items-center gap-1 px-2 py-1.5" type="submit">
			{isLoading ? <LoadingCircles width={33} height={33}/> : <Icon icon={DareDropIcon} size={33}/>}
			<span className="font-bold uppercase text-xl">
				{label}
			</span>
		</ButtonNew>);
});
function getDeliveryRatioColor(ratio) {
    return Object.entries(deliveryRatioColor)
        .reduce((acc, [threshold, text]) => {
        return ratio >= Number(threshold) ? text : acc;
    }, deliveryRatioColor['0']);
}
